import React, { useEffect, useCallback } from 'react';
import { Box, Flex, Text, Button, Icon, useDisclosure } from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import { PiPencil } from 'react-icons/pi';
import { createColumnHelper } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CommonTable from 'components/commonTable/CommonTable';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import {
  GetLoginUserRegionData,
  AddRegionData,
} from 'reactRedux/thunks/regionEditThunk/RegionEditThunk';
import { sideBarRoutes } from 'utils/StaticData';
import AddRegionModal from './AddRegionModal';
import OnOffBadge from './OnOffBadge';
import { DEDUCTION_FIELDS } from './constants';

const headerText = (label) => (
  <Text
    whiteSpace="nowrap"
    align="left"
    fontSize={{ sm: '10px', lg: '12px' }}
    color="gray.400"
    fontFamily="Montserrat"
  >
    {label}
  </Text>
);

const RegionalSettingsList = () => {
  const dispatch = useDispatch();
  const { tabletextColor } = useColorModeVariables();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const { isLoading, getRegionData, isAddLoading } = useSelector(
    (state) => state.RegionEditSlice,
  );
  const isAdmin = adminDetails?.role === sideBarRoutes?.Admin;

  const getRegions = useCallback(() => {
    dispatch(GetLoginUserRegionData());
  }, [dispatch]);

  useEffect(() => {
    getRegions();
  }, [getRegions]);

  const handleAddRegion = (payload, resetForm) => {
    dispatch(
      AddRegionData(payload, () => {
        resetForm();
        onClose();
        getRegions();
      }),
    );
  };

  const tableData = Array.isArray(getRegionData) ? getRegionData : [];
  const columnHelper = createColumnHelper();

  const valueCell = (info) => (
    <Text color={tabletextColor} fontSize="sm" fontWeight="600" align="left">
      {info.getValue() ?? 'N/A'}
    </Text>
  );

  const columns = [
    columnHelper.accessor((row) => row.regionId, {
      id: 'regionId',
      header: () => headerText('ID'),
      cell: valueCell,
    }),
    columnHelper.accessor((row) => row.regionName, {
      id: 'regionName',
      header: () => headerText('Region'),
      cell: valueCell,
    }),
    ...DEDUCTION_FIELDS.map((deduction) =>
      columnHelper.accessor((row) => row[deduction.field], {
        id: deduction.field,
        header: () => headerText(deduction.short),
        cell: (info) => <OnOffBadge value={Boolean(info.getValue())} />,
      }),
    ),
    columnHelper.accessor((row) => row.regionId, {
      id: 'actions',
      header: () => headerText('ACTION'),
      cell: (info) => (
        <Flex gap="10px">
          <Link
            to={`/admin/regional-settings/edit/${info.getValue()}`}
            className="btn btn-light rounded-3 p-1 no-navigate"
          >
            <PiPencil size={20} title="Edit" />
          </Link>
        </Flex>
      ),
    }),
  ];

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        {isAdmin && (
          <Flex justify="flex-end" align="center" mb={4} flexWrap="wrap">
            <Button
              bg="reviveOrange"
              color="primary"
              borderRadius="12px"
              _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
              onClick={onOpen}
            >
              <Icon as={FaPlus} width="15px" height="15px" marginRight="5px" />
              Add Region
            </Button>
          </Flex>
        )}

        <CommonTable
          columns={columns}
          data={tableData}
          keyValue="regionId"
          isLink={true}
          isLoading={isLoading}
          parentLink="/admin/regional-settings/details"
          rowclick={['actions']}
          dynamicWidth={{
            regionId: '70px',
            regionName: '160px',
            defaultIncludePowerbankCost: '140px',
            defaultIncludeCustomerPurchases: '160px',
            defaultIncludeTransactionCost: '150px',
            defaultIncludeEInvoiceFee: '130px',
            actions: '90px',
          }}
        />
      </Box>

      <AddRegionModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleAddRegion}
        isLoading={isAddLoading}
        adminDetails={adminDetails}
      />
    </>
  );
};

export default RegionalSettingsList;
